// context/LanguageContext.js
// Sistem multi-bahasa (i18n) ringan tanpa library tambahan.
// Pilihan bahasa disimpan di localStorage agar persisten antar sesi.
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { translations } from './translations';

const LanguageContext = createContext(null);
const STORAGE_KEY = 'app_lang';

// Bahasa awal untuk pengguna yang belum pernah memilih.
const DEFAULT_LANG = 'en';

// Bahasa cadangan saat sebuah key tidak ada di bahasa aktif.
// Sengaja BUKAN 'id': dulu key yang hilang jatuh ke bahasa Indonesia, sehingga
// UI berbahasa Arab diam-diam menampilkan teks Indonesia tanpa error apa pun —
// begitulah 153 key Arab sempat hilang tanpa ketahuan. Inggris jauh lebih
// mungkin terbaca oleh pengguna non-Indonesia.
const FALLBACK_LANG = 'en';

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG; }
    catch { return DEFAULT_LANG; }
  });

  const setLang = useCallback((l) => {
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch (_) {}
  }, []);

  // t('key') → teks sesuai bahasa aktif.
  // t('key', { name: 'Budi' }) → ganti {name} di string dengan nilai.
  // Fallback: bahasa aktif → Inggris → key mentah (supaya tidak pernah kosong).
  //
  // Rantai ini berhenti di key mentah, bukan mundur lagi ke Indonesia. Key
  // mentah memang jelek dilihat, tapi justru itu gunanya: teks seperti
  // 'notes.newNote' langsung terlihat salah, sedangkan teks Indonesia yang
  // nyasar ke UI Inggris/Arab bisa bertahan berbulan-bulan tanpa disadari.
  const t = useCallback((key, vars) => {
    const dict = translations[lang] || translations[FALLBACK_LANG] || {};
    let str = dict[key];
    if (str === undefined) {
      const fbDict = translations[FALLBACK_LANG] || {};
      str = fbDict[key] !== undefined ? fbDict[key] : key;
    }
    if (vars && typeof str === 'string') {
      Object.keys(vars).forEach(k => {
        str = str.replace(new RegExp('\\{' + k + '\\}', 'g'), vars[k]);
      });
    }
    return str;
  }, [lang]);

  // Locale untuk format tanggal/angka sesuai bahasa aktif.
  // ar: pakai 'ar-u-nu-latn' → teks Arab tapi digit tetap Latin (0-9), supaya
  // angka/tanggal tetap terbaca konsisten dengan mode RTL parsial.
  const locale = lang === 'en' ? 'en-US' : lang === 'ar' ? 'ar-u-nu-latn' : 'id-ID';

  // Set penanda bahasa + class .lang-ar di root (untuk font Arab).
  // CATATAN: dir="rtl" SENGAJA TIDAK di-set global — kalau di <html>, seluruh
  // struktur (sidebar/header/flex) ikut ter-flip. RTL diterapkan per-kontainer
  // konten pada Tahap 3, supaya kerangka dashboard tetap normal.
  useEffect(() => {
    const isAr = lang === 'ar';
    const root = document.documentElement;
    root.setAttribute('lang', lang);
    root.setAttribute('dir', 'ltr'); // paksa LTR di root; RTL parsial menyusul
    root.classList.toggle('lang-ar', isAr);
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, locale }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang harus dipakai di dalam <LanguageProvider>');
  return ctx;
}
